import { useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAppDispatch } from "@/core/hooks";
import { store } from "@/core/store";
import {
  pushModal,
  popModal,
  setModalLoading,
  setModalError,
  clearModalState,
  initializeModalState,
} from "@/core/modal";
import {
  selectHasModal,
  selectIsModalLoading,
  selectModalError,
} from "@/core/modal";
import type {
  ModalRouteParams,
  ModalBackgroundState,
  ModalRouterActions,
} from "@/types/modal";
import type { ModalStackItem } from "@/core/modal/types";

/**
 * Enhanced Modal router hook for React Router integration
 * Now uses centralized Redux state instead of local useModalStack
 *
 * Based on Architecture Design: Centralized State Management
 * - Uses Redux for global modal state
 * - No more stale closure issues
 * - Consistent state across all components
 */
export const useModalRouter = (): ModalRouterActions => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  // Helper function to clean up inactive modals
  const cleanupInactiveModals = useCallback(() => {
    const state = store.getState();
    const currentPath = location.pathname;

    const inactiveModals = state.modal.stack.filter((modal) => {
      // Build expected modal path for this modal
      const modalPath = modal.id.includes("-")
        ? `/modal/${modal.type}/${modal.id.split("-")[1]}`
        : `/modal/${modal.type}`;

      // Modal is inactive if its path doesn't match current path
      return modalPath !== currentPath;
    });

    if (inactiveModals.length > 0) {
      console.log(
        "🔍 useModalRouter cleanupInactiveModals - cleaning up:",
        inactiveModals.map((m) => m.id)
      );
      inactiveModals.forEach((modal) => {
        dispatch(popModal(modal.id));
      });
    }
  }, [location.pathname, dispatch]);

  // Navigate to modal route with enhanced state management
  const navigateToModal = useCallback(
    (type: string, id?: string, urlParams?: Record<string, string>): void => {
      // Build route ID for existence checking and state management
      const routeId = id ? `${type}-${id}` : type;
      let modalPath = id ? `/modal/${type}/${id}` : `/modal/${type}`;

      // Add URL parameters if provided
      if (urlParams && Object.keys(urlParams).length > 0) {
        const searchParams = new URLSearchParams(urlParams);
        modalPath += `?${searchParams.toString()}`;
      }

      // Check if modal already exists in stack using store.getState() for synchronous access
      const state = store.getState();
      const hasModal = selectHasModal(state, routeId);

      if (hasModal) {
        // Check if the modal is currently active (visible in UI)
        const isCurrentlyActive = location.pathname === modalPath;

        if (isCurrentlyActive) {
          console.warn(`Modal ${routeId} is already active and visible`);
          return;
        } else {
          // Modal exists in stack but is not currently active
          // This can happen when a modal was opened but then another modal was opened on top
          // We should clean up the inactive modal and allow reopening
          console.log(
            `Modal ${routeId} exists in stack but is not active, cleaning up...`
          );
          dispatch(popModal(routeId));
        }
      }

      // Additional cleanup: remove any other inactive modals
      cleanupInactiveModals();

      // Create modal stack item using routeId as the primary identifier
      const modalItem: Omit<ModalStackItem, "zIndex"> = {
        id: routeId, // Use routeId as the primary identifier
        type,
        routeId,
        timestamp: Date.now(),
      };

      // Add to modal stack via Redux
      dispatch(pushModal(modalItem));

      // Enhanced background state management
      // If we're already in a modal route, preserve the current modal as background
      // If we're on the main page, use the current location as background
      const currentBackground = location.state?.background || {
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
        state: location.state,
      };

      // Add metadata to background state for better tracking
      const enhancedBackground = {
        ...currentBackground,
        metadata: {
          timestamp: Date.now(),
          modalId: routeId,
          isModalRoute: location.pathname.startsWith("/modal/"),
        },
      };

      navigate(modalPath, {
        state: {
          background: enhancedBackground,
          modalId: routeId, // Pass modal ID for state management
        },
      });
    },
    [navigate, location, dispatch, cleanupInactiveModals]
  );

  // Enhanced navigate back with Redux state cleanup
  const navigateBack = useCallback(() => {
    const backgroundState = getBackgroundState();

    // Get current modal ID from location state
    const currentModalId = location.state?.modalId;

    // Clean up current modal from stack
    if (currentModalId) {
      dispatch(popModal(currentModalId));
    }

    // Clean up any inactive modals
    cleanupInactiveModals();

    if (backgroundState) {
      // Navigate back to the background route
      const backgroundPath =
        backgroundState.pathname +
        (backgroundState.search || "") +
        (backgroundState.hash || "");

      navigate(backgroundPath, {
        state: backgroundState.state,
        replace: true, // Replace current modal route in history
      });
    } else {
      // Fallback: go back in history
      console.warn("No background state found, falling back to history.back()");
      navigate(-1);
    }
  }, [navigate, location.state, dispatch, cleanupInactiveModals]);

  // Get current modal route params
  const getModalParams = useCallback((): ModalRouteParams | null => {
    // Check if we're in a modal route
    if (!location.pathname.startsWith("/modal/")) {
      return null;
    }

    // Parse modal route: /modal/{type}/{id}
    const pathParts = location.pathname.split("/");
    if (pathParts.length < 3) {
      return null;
    }

    const type = pathParts[2];
    const id = pathParts[3] || undefined;

    // Parse URL search parameters
    const searchParams = new URLSearchParams(location.search);
    const urlParams: Record<string, string> = {};

    searchParams.forEach((value, key) => {
      urlParams[key] = value;
    });

    return {
      type,
      id,
      ...urlParams,
    };
  }, [location.pathname, location.search]);

  // Check if currently in modal route
  const isModalRoute = useCallback((): boolean => {
    return location.pathname.startsWith("/modal/");
  }, [location.pathname]);

  // Get background route state
  const getBackgroundState = useCallback((): ModalBackgroundState | null => {
    return location.state?.background || null;
  }, [location.state]);

  // State management methods using Redux
  const setLoading = useCallback(
    (modalId: string, loading: boolean) => {
      dispatch(setModalLoading({ modalId, loading }));
    },
    [dispatch]
  );

  const clearLoading = useCallback(
    (modalId: string) => {
      dispatch(setModalLoading({ modalId, loading: false }));
    },
    [dispatch]
  );

  const setError = useCallback(
    (modalId: string, error: Error | null) => {
      dispatch(setModalError({ modalId, error }));
    },
    [dispatch]
  );

  const clearError = useCallback(
    (modalId: string) => {
      dispatch(setModalError({ modalId, error: null }));
    },
    [dispatch]
  );

  // Fixed: Use synchronous store access instead of hooks in callbacks
  const isLoading = useCallback((modalId?: string): boolean => {
    const state = store.getState();
    if (modalId) {
      return selectIsModalLoading(state, modalId);
    }
    // Check if any modal is loading
    return Object.values(state.modal.modalStates).some(
      (modalState) => modalState.loading
    );
  }, []);

  const getError = useCallback((modalId?: string): Error | null => {
    const state = store.getState();
    if (modalId) {
      return selectModalError(state, modalId);
    }
    // Return first error found
    for (const modalState of Object.values(state.modal.modalStates)) {
      if (modalState.error) return modalState.error;
    }
    return null;
  }, []);

  const clearModalStateHandler = useCallback(
    (modalId: string) => {
      dispatch(clearModalState(modalId));
    },
    [dispatch]
  );

  const initializeModalStateHandler = useCallback(
    (modalId: string) => {
      dispatch(initializeModalState(modalId));
    },
    [dispatch]
  );

  return {
    // Navigation
    navigateToModal,
    navigateBack,
    isModalRoute,
    getModalParams,
    getBackgroundState,

    // State management
    setLoading,
    clearLoading,
    setError,
    clearError,
    isLoading,
    getError,
    clearModalState: clearModalStateHandler,
    initializeModalState: initializeModalStateHandler,
  };
};

/**
 * Alternative hook with simpler API for basic modal operations
 */
export const useModalWithRouter = () => {
  const router = useModalRouter();

  return {
    openModal: router.navigateToModal,
    closeModal: router.navigateBack,
    isInModal: router.isModalRoute,
    setLoading: router.setLoading,
    setError: router.setError,
  };
};
